import type { Scale } from '@tunasong/schemas'
import type { Chord } from '../chord/index.js'
import { getBorrowedFunction, isInParallelMixolydian } from './borrowing.js'
import { getDiatonicFunction, isDominantSeventhVariant } from './diatonic.js'

/**
 * Identify which parallel mode a borrowed chord belongs to, returning a
 * lexicon id (no inline copy) so the rendering surface can level-gate via
 * `useTheoryCopy`. Returns null when the chord is diatonic, when the
 * borrowing is unrecognised, or when the borrowing falls outside Phase-1
 * coverage (minor-key borrowings).
 *
 * The lexicon ids returned here name new "borrowed-*" entries that ship
 * under Bowie's authorship; see `lib/music-lib/src/lexicon/lexicon-entries.ts`.
 * If a borrowing matches a recognised function but no lexicon entry is
 * present, the surface gracefully hides the line — `useTheoryCopy(id)`
 * returns null for unknown ids by design.
 *
 * Phase-1 coverage (per the spec):
 *   - ♭VII in major → mixolydian flavour, when every tone is in the parallel
 *     Mixolydian; otherwise parallel minor. B♭7 / B♭9 in C (A♭ is Aeolian) are
 *     the backdoor dominant (GH #2164); any other gets a null `lexiconId`
 *   - ♭III, iv, ♭VI in major → parallel-minor flavour
 *   - ♭II in major → phrygian (Neapolitan) flavour
 *   - Minor-key borrowings → null (deferred to a later milestone)
 */
/** Lexicon id for the ♭VII7 backdoor dominant borrowed from the parallel minor (GH #2164). */
export const BACKDOOR_DOMINANT_LEXICON_ID = 'borrowed-flat-VII7-major'

export interface ParallelModeLabel {
  sourceMode: 'mixolydian' | 'parallel-minor' | 'phrygian'
  lexiconId: string | null
}

const MAJOR_BORROWING_MAP: Record<string, ParallelModeLabel> = {
  '♭VII': { sourceMode: 'mixolydian', lexiconId: 'borrowed-flat-VII-major' },
  iv: { sourceMode: 'parallel-minor', lexiconId: 'borrowed-iv-major' },
  '♭III': { sourceMode: 'parallel-minor', lexiconId: 'borrowed-flat-III-major' },
  '♭VI': { sourceMode: 'parallel-minor', lexiconId: 'borrowed-flat-VI-major' },
  '♭II': { sourceMode: 'phrygian', lexiconId: 'borrowed-flat-II-major' },
}

export const getParallelModeLabel = (chord: Chord, scale: Scale): ParallelModeLabel | null => {
  // Diatonic chords aren't borrowed by definition. This guard also covers the
  // "Eb in C minor is the diatonic ♭III" case the test exercises.
  if (getDiatonicFunction(chord, scale)) {
    return null
  }
  const borrowed = getBorrowedFunction(chord, scale)
  if (!borrowed) {
    return null
  }
  // Phase-1 ships only major-key borrowing labels. Minor-key borrowings
  // resolve via getBorrowedFunction but have no lexicon entry yet — null is
  // the right answer (Bowie register-rules invariant: null > wrong).
  if (scale.type !== 'major') {
    return null
  }
  if (borrowed.chordFunction === '♭VII' && !isInParallelMixolydian(chord, scale)) {
    return {
      sourceMode: 'parallel-minor',
      lexiconId: isDominantSeventhVariant(chord) ? BACKDOOR_DOMINANT_LEXICON_ID : null,
    }
  }
  return MAJOR_BORROWING_MAP[borrowed.chordFunction] ?? null
}
